<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  contactInboxes: { type: Array, default: () => [] },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'updateContact', 'showContact']);

const CHANNEL_CONFIG = {
  'Channel::Instagram': {
    icon: 'i-ri-instagram-fill',
    color: '#E1306C',
    bg: 'rgba(225,48,108,0.10)',
  },
  'Channel::Tiktok': {
    icon: 'i-ri-tiktok-fill',
    color: '#000000',
    bg: 'rgba(0,0,0,0.08)',
  },
  'Channel::FacebookPage': {
    icon: 'i-ri-messenger-fill',
    color: '#1877F2',
    bg: 'rgba(24,119,242,0.10)',
  },
  'Channel::Whatsapp': {
    icon: 'i-ri-whatsapp-fill',
    color: '#25D366',
    bg: 'rgba(37,211,102,0.10)',
  },
  'Channel::Telegram': {
    icon: 'i-ri-telegram-fill',
    color: '#0088CC',
    bg: 'rgba(0,136,204,0.10)',
  },
  'Channel::TwitterProfile': {
    icon: 'i-ri-twitter-x-fill',
    color: '#000000',
    bg: 'rgba(0,0,0,0.08)',
  },
  'Channel::Email': {
    icon: 'i-ri-mail-fill',
    color: '#EA4335',
    bg: 'rgba(234,67,53,0.10)',
  },
  'Channel::WebWidget': {
    icon: 'i-ri-global-fill',
    color: '#6366f1',
    bg: 'rgba(99,102,241,0.10)',
  },
  'Channel::Line': {
    icon: 'i-ri-line-fill',
    color: '#00C300',
    bg: 'rgba(0,195,0,0.10)',
  },
  'Channel::Sms': {
    icon: 'i-ri-chat-1-fill',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.10)',
  },
  'Channel::TwilioSms': {
    icon: 'i-ri-chat-1-fill',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.10)',
  },
  'Channel::Api': {
    icon: 'i-ri-cloudy-fill',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.10)',
  },
  'Channel::Voice': {
    icon: 'i-ri-phone-fill',
    color: '#8b5cf6',
    bg: 'rgba(139,92,246,0.10)',
  },
};

const DEFAULT_CHANNEL_CONFIG = {
  icon: 'i-ri-global-fill',
  color: '#6b7280',
  bg: 'rgba(107,114,128,0.10)',
};

const MAX_VISIBLE_BADGES = 3;

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.code] = country;
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const channelBadges = computed(() => {
  return (props.contactInboxes || []).map(ci => {
    const channelType = ci.inbox?.channelType || ci.inbox?.channel_type;
    const config = CHANNEL_CONFIG[channelType] || DEFAULT_CHANNEL_CONFIG;
    return {
      key: ci.id,
      name: ci.inbox?.name || channelType,
      icon: config.icon,
      color: config.color,
      bg: config.bg,
    };
  });
});

const visibleBadges = computed(() =>
  channelBadges.value.slice(0, MAX_VISIBLE_BADGES)
);
const overflowCount = computed(() =>
  Math.max(0, channelBadges.value.length - MAX_VISIBLE_BADGES)
);

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);
</script>

<template>
  <CardLayout :key="id" layout="row">
    <div class="flex items-center justify-start flex-1 gap-4">
      <Avatar
        :name="name"
        :src="thumbnail"
        :size="48"
        :status="availabilityStatus"
        rounded-full
      />
      <div class="flex flex-col gap-0.5 flex-1">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ name }}
          </span>
          <span class="inline-flex items-center gap-1">
            <span
              v-if="additionalAttributes?.companyName"
              class="i-ph-building-light size-4 text-n-slate-10 mb-0.5"
            />
            <span
              v-if="additionalAttributes?.companyName"
              class="text-sm truncate text-n-slate-11"
            >
              {{ additionalAttributes.companyName }}
            </span>
          </span>
        </div>
        <div class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1">
          <div v-if="email" class="truncate max-w-72" :title="email">
            <span class="text-sm text-n-slate-11">
              {{ email }}
            </span>
          </div>
          <div v-if="email" class="w-px h-3 truncate bg-n-slate-6" />
          <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
            {{ phoneNumber }}
          </span>
          <div v-if="phoneNumber" class="w-px h-3 truncate bg-n-slate-6" />
          <span
            v-if="countryDetails"
            class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
          >
            <Flag :country="countryDetails.countryCode" class="size-3.5" />
            {{ formattedLocation }}
          </span>
          <div v-if="countryDetails" class="w-px h-3 truncate bg-n-slate-6" />
          <template v-if="visibleBadges.length">
            <div class="flex items-center gap-1.5">
              <span
                v-for="badge in visibleBadges"
                :key="badge.key"
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium leading-none"
                :style="{ background: badge.bg, color: badge.color }"
                :title="badge.name"
              >
                <span class="size-3 shrink-0" :class="[badge.icon]" />
                <span class="truncate max-w-20">{{ badge.name }}</span>
              </span>
              <span
                v-if="overflowCount > 0"
                class="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium text-n-slate-10 bg-n-slate-3"
              >
                +{{ overflowCount }}
              </span>
            </div>
            <div class="w-px h-3 truncate bg-n-slate-6" />
          </template>
          <Button
            :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
            variant="link"
            size="xs"
            @click="onClickViewDetails"
          />
        </div>
      </div>
    </div>

    <Button
      icon="i-lucide-chevron-down"
      variant="ghost"
      color="slate"
      size="xs"
      :class="{ 'rotate-180': isExpanded }"
      @click="onClickExpand"
    />

    <template #after>
      <div
        class="transition-all duration-500 ease-in-out grid overflow-hidden"
        :class="
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        "
      >
        <div class="overflow-hidden">
          <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
            <ContactsForm
              ref="contactsFormRef"
              :contact-data="contactData"
              @update="handleFormUpdate"
            />
            <div>
              <Button
                :label="
                  t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                "
                size="sm"
                :is-loading="isUpdating"
                :disabled="isUpdating || isFormInvalid"
                @click="handleUpdateContact"
              />
            </div>
          </div>
          <ContactDeleteSection
            :selected-contact="{
              id: props.id,
              name: props.name,
            }"
          />
        </div>
      </div>
    </template>
  </CardLayout>
</template>
